import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Text, TEXT_VARIANTS } from 'components/ui/Typography';
import {
  Button,
  Popover,
  ActionList,
  ButtonGroup,
  Icon,
  Skeleton,
} from 'components/ui';
import InfoTooltip from 'components/shared/InfoTooltip';
import { REACT_QUERY_STATUS, SCREEN_SIZES } from 'constants/config';
import { withSkillsClientWrapper } from 'components/shared/Skills/SkillsClientProvider';
import { withReactQueryWrapper } from 'components/shared/ReactQuery/ReactQueryProvider';
import { Star, Edit3, Circle, Info } from 'react-feather';
import useWindowSize from 'hooks/useWindowSize';
import StatusLabel from '../shared/StatusLabel';
import { useClearNotificationEffect } from '../shared/hooks';
import {
  useGetGlobalSkillsSettings,
  useUpdateGlobalSkillsSettings,
} from './queries/globalSkillsSettings';
import ConfirmationModal from './confirmationModal';

const GlobalSkillsSettings = () => {
  const queryClient = useQueryClient();
  const { data, status } = useGetGlobalSkillsSettings();
  const currentCompanyRatingDefinitions =
    data?.skillsSettings?.ratingDefinitions ?? '';
  const currentCompanyRatingIconStyle =
    data?.skillsSettings?.ratingIconStyle ?? 'skills_default';
  const currentCompanyRatingScale = data?.skillsSettings?.ratingScale;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [tempIconChoice, setTempIconChoice] = useState('');
  const [saveClicked, setSaveClicked] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const size = useWindowSize();
  const isMobile = size.width < SCREEN_SIZES.MD;
  const [tempRatingScale, setTempRatingScale] = useState(
    currentCompanyRatingScale,
  );
  const [minLevelMessage, setMinLevelMessage] = useState(false);
  const [maxLevelMessage, setMaxLevelMessage] = useState(false);
  const [levelButtonShowing, setLevelButtonShowing] = useState(false);
  const [tempRatingDefinition, setTempRatingDefinition] = useState({
    one: '',
    two: '',
    three: '',
    four: '',
    five: '',
    six: '',
    seven: '',
    eight: '',
    nine: '',
    ten: '',
  });
  const [ratingDefinitionsStatus, setRatingDefinitionsStatus] = useState({
    one: { error: false, focus: false },
    two: { error: false, focus: false },
    three: { error: false, focus: false },
    four: { error: false, focus: false },
    five: { error: false, focus: false },
    six: { error: false, focus: false },
    seven: { error: false, focus: false },
    eight: { error: false, focus: false },
    nine: { error: false, focus: false },
    ten: { error: false, focus: false },
  });

  const { mutate } = useUpdateGlobalSkillsSettings({
    ratingIconStyle: '',
    ratingDefinitions: {},
    ratingScale: '',
  });
  const pageIsLoading = status === REACT_QUERY_STATUS.LOADING;

  useEffect(() => {
    setTempIconChoice(currentCompanyRatingIconStyle);
    setTempRatingScale(currentCompanyRatingScale);
    if (currentCompanyRatingDefinitions?.one !== undefined) {
      setTempRatingDefinition(currentCompanyRatingDefinitions);
    }
  }, [
    currentCompanyRatingIconStyle,
    currentCompanyRatingDefinitions,
    currentCompanyRatingScale,
  ]);

  const clearSetIsSuccess = useCallback(() => {
    setSaveSuccess(false);
  }, []);

  useClearNotificationEffect(saveSuccess, null, clearSetIsSuccess, 5000);

  const shouldDisableSave = useMemo(() => {
    const newRatingDefinitions = Object.fromEntries(
      Object.entries(ratingDefinitionsStatus).slice(0, tempRatingScale),
    );
    return Object.values(newRatingDefinitions).some(v => {
      return v.error !== false;
    });
  }, [ratingDefinitionsStatus, tempRatingScale]);

  const trimTempRatingDefinitions = () => {
    const trimmedRatingDefinitions = tempRatingDefinition;
    const ratingDefinitionKeys = Object.keys(tempRatingDefinition);
    ratingDefinitionKeys.forEach(key => {
      if (tempRatingDefinition[key])
        trimmedRatingDefinitions[key] = tempRatingDefinition[key].trim();
    });
    setTempRatingDefinition(trimmedRatingDefinitions);
  };

  async function handleSubmit() {
    if (tempRatingScale !== currentCompanyRatingScale) {
      setSaveClicked(true);
    } else {
      trimTempRatingDefinitions();
      const newRatingDefinitions = Object.fromEntries(
        Object.entries(tempRatingDefinition).slice(0, tempRatingScale),
      );
      try {
        await mutate(
          {
            ratingIconStyle: tempIconChoice,
            ratingDefinitions: JSON.stringify(newRatingDefinitions),
            ratingScale: currentCompanyRatingScale,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries('skillsSettings');
              setSaveSuccess(true);
            },
          },
        );
      } catch (err) {
        console.error(err);
      }
      setEditMode(false);
    }
  }

  const closeModal = () => {
    setSaveClicked(false);
  };

  const findNumberWord = number => {
    switch (number) {
      case 1:
        return 'one';
      case 2:
        return 'two';
      case 3:
        return 'three';
      case 4:
        return 'four';
      case 5:
        return 'five';
      case 6:
        return 'six';
      case 7:
        return 'seven';
      case 8:
        return 'eight';
      case 9:
        return 'nine';
      case 10:
        return 'ten';
      default:
        return 'five';
    }
  };

  useEffect(() => {
    if (tempRatingScale === 2) {
      setMinLevelMessage(true);
    }
    if (tempRatingScale >= 3) {
      setMinLevelMessage(false);
    }
    if (tempRatingScale === 10) {
      setMaxLevelMessage(true);
      setLevelButtonShowing(false);
    }
    if (tempRatingScale <= 9) {
      setMaxLevelMessage(false);
      setLevelButtonShowing(true);
    }
  }, [tempRatingScale]);

  const handleDelete = () => {
    setTempRatingScale(tempRatingScale - 1);
  };

  const handleEditButton = () => {
    setEditMode(!editMode);
    setSaveSuccess(false);
    setLevelButtonShowing(true);
    queryClient.invalidateQueries('skillsSettings');
  };

  const handleCancel = () => {
    setEditMode(false);
    setTempIconChoice(currentCompanyRatingIconStyle);
    setTempRatingScale(currentCompanyRatingScale);
    setTempRatingDefinition(currentCompanyRatingDefinitions);
    setRatingDefinitionsStatus({
      one: { error: false, focus: false },
      two: { error: false, focus: false },
      three: { error: false, focus: false },
      four: { error: false, focus: false },
      five: { error: false, focus: false },
      six: { error: false, focus: false },
      seven: { error: false, focus: false },
      eight: { error: false, focus: false },
      nine: { error: false, focus: false },
      ten: { error: false, focus: false },
    });
  };

  const specialChars = '[`!@#$%^&*()_+-=[]{};\':"\\|,.<>/?~]/';

  function ratingDefinitionHasError(definition) {
    if (
      Object.values(tempRatingDefinition)?.filter(
        def =>
          def?.toLowerCase() === definition?.toLowerCase() &&
          definition?.length > 0,
      ).length > 0
    ) {
      return { message: 'duplicate' };
    }

    const re = new RegExp(
      '\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff]',
    );
    if (
      specialChars
        .split('')
        .some(specialChar => definition?.includes(specialChar)) ||
      re.test(definition)
    ) {
      return { message: 'special' };
    }
    if (definition?.length > 40) {
      return { message: 'long' };
    }
    if (definition?.length === 0 || definition === null) {
      return { message: 'short' };
    }
    return false;
  }

  const handleAddLevel = () => {
    const newRatingScale = tempRatingScale + 1;
    const currentNumberWord = findNumberWord(newRatingScale);
    setTempRatingScale(newRatingScale);
    setRatingDefinitionsStatus(prev => ({
      ...prev,
      [currentNumberWord]: {
        error: {
          message: 'short',
        },
        focus: true,
      },
    }));
  };

  const renderRatings = (number, numberWord) => {
    const ratingDefinitions = Object.values(
      currentCompanyRatingDefinitions ?? [],
    );

    function ratingText() {
      const defaultText = {
        1: t('grades.ratings.unsatisfactory'),
        2: t('grades.ratings.needs_improvement'),
        3: t('grades.ratings.meets_expectations'),
        4: t('grades.ratings.exceeds_expectations'),
        5: t('grades.ratings.exceptional'),
        6: t('settings.tab.global_skills.type_proficiency_level'),
      };

      if (number <= 5) {
        return ratingDefinitions[number - 1]
          ? `${number} or ${ratingDefinitions[number - 1]}`
          : `${number} or ${defaultText[number]}`;
      }
      return ratingDefinitions[number - 1]
        ? `${number} or ${ratingDefinitions[number - 1]}`
        : defaultText[6];
    }

    return (
      <>
        <div className={isMobile ? 'd-f ai-c fd-c' : 'd-f ai-c fd-r'}>
          {editMode ? (
            <div className="d-f fd-c">
              <div
                className={
                  tempRatingDefinition[numberWord]?.length > 0 &&
                  tempRatingDefinition[numberWord]?.length <= 40 &&
                  ratingDefinitionsStatus[numberWord]?.focus
                    ? 'd-f w-350 ai-c mt-4'
                    : 'd-f w-350 ai-c'
                }
              >
                <Text
                  variant={TEXT_VARIANTS.BODY}
                  className={
                    number !== 9 && number !== 8 && number !== 7 ? '' : 'mr-2'
                  }
                >
                  {number}
                </Text>
                <span
                  className={
                    number === 1
                      ? 'llui-text-instrux-error ml-6 mr-2'
                      : 'llui-text-instrux-error ml-4 mr-1'
                  }
                >
                  *
                </span>
                <input
                  placeholder={ratingText(number, ratingDefinitions)}
                  onChange={e => {
                    setTempRatingDefinition(prev => ({
                      ...prev,
                      [numberWord]: e.target.value,
                    }));
                    setRatingDefinitionsStatus(prev => ({
                      ...prev,
                      [numberWord]: {
                        error: ratingDefinitionHasError(e.target.value),
                        focus: true,
                      },
                    }));
                  }}
                  value={tempRatingDefinition[numberWord]}
                  id={tempRatingDefinition[numberWord]}
                  onFocus={() => {
                    setRatingDefinitionsStatus(prev => ({
                      ...prev,
                      [numberWord]: {
                        error: ratingDefinitionsStatus[numberWord]?.error,
                        focus: true,
                      },
                    }));
                  }}
                  onBlur={() => {
                    setRatingDefinitionsStatus(prev => ({
                      ...prev,
                      [numberWord]: {
                        error: ratingDefinitionsStatus[numberWord].error,
                        focus: false,
                      },
                    }));
                  }}
                  className={
                    number === 10
                      ? 'fz-16 h-40 ml-8 w-350 p-10'
                      : 'fz-16 h-40 ml-12 w-350 p-10'
                  }
                />
              </div>
              {ratingDefinitionsStatus[numberWord]?.error &&
                ratingDefinitionsStatus[numberWord]?.error.message !==
                  'short' && (
                  <div className="d-f ml-34 jc-sb">
                    {ratingDefinitionsStatus[numberWord]?.error.message ===
                      'duplicate' && (
                      <div className="mt-16">
                        <Info
                          className="mr-8 llui-text-instrux-error is-16"
                          size={17}
                        />
                        <Text variant={TEXT_VARIANTS.INSTRUX_ERROR}>
                          {t('settings.tab.global_skills.duplicate_label')}
                        </Text>
                      </div>
                    )}
                    {ratingDefinitionsStatus[numberWord]?.error.message ===
                      'special' && (
                      <div className="mt-16">
                        <Info
                          className="mr-8 llui-text-instrux-error is-16"
                          size={17}
                        />
                        <Text variant={TEXT_VARIANTS.INSTRUX_ERROR}>
                          {t(
                            'settings.tab.global_skills.no_special_characters',
                          )}
                        </Text>
                      </div>
                    )}
                    {ratingDefinitionsStatus[numberWord]?.error.message ===
                      'long' && (
                      <div className="mt-16">
                        <Info
                          className="mr-8 llui-text-instrux-error is-16"
                          size={17}
                        />
                        <Text variant={TEXT_VARIANTS.INSTRUX_ERROR}>
                          {t('settings.tab.global_skills.max_character_count')}
                        </Text>
                      </div>
                    )}
                    <div className="d-f mt-16">
                      {ratingDefinitionsStatus[numberWord]?.error &&
                        ratingDefinitionsStatus[numberWord]?.error.message !==
                          'short' && (
                          <Text
                            variant={
                              ratingDefinitionsStatus[numberWord]?.error
                                .message === 'long'
                                ? TEXT_VARIANTS.INSTRUX_ERROR
                                : TEXT_VARIANTS.BODY_LIGHT
                            }
                          >
                            {`${tempRatingDefinition[numberWord]?.length}/40`}
                          </Text>
                        )}
                    </div>
                  </div>
                )}
              {ratingDefinitionsStatus[numberWord]?.focus &&
                tempRatingDefinition[numberWord]?.length > 0 &&
                tempRatingDefinition[numberWord]?.length <= 40 &&
                !ratingDefinitionsStatus[numberWord]?.error && (
                  <div className="d-f fd-rr jc-fe mt-16">
                    <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                      {`${tempRatingDefinition[numberWord]?.length}/40`}
                    </Text>
                  </div>
                )}
            </div>
          ) : (
            <>
              <div className="d-f fd-c w-350">
                <input
                  disabled={!editMode}
                  variant="filled"
                  placeholder={ratingText(number, ratingDefinitions)}
                  className="fz-16 h-40 pl-10"
                  id={tempRatingDefinition[numberWord]}
                />
              </div>
            </>
          )}
          {tempIconChoice === 'skills_default' ? (
            <div
              className={
                (ratingDefinitionsStatus[numberWord]?.error.message &&
                  ratingDefinitionsStatus[numberWord]?.error.message !==
                    'short') ||
                (ratingDefinitionsStatus[numberWord]?.focus &&
                  tempRatingDefinition[numberWord] !== null &&
                  tempRatingDefinition[numberWord] !== '')
                  ? 'd-f mt-16 ai-c fd-r mb-48'
                  : 'd-f mt-16 ai-c fd-r mb-12'
              }
            >
              <div className="mr-8 ml-24">
                <Star className="CustomColor--svgFill" stroke="#949499" />
              </div>
              <div className="mr-8">
                <Star
                  className={number >= 2 ? 'CustomColor--svgFill' : ''}
                  stroke="#949499"
                />
              </div>
              {tempRatingScale >= 3 && (
                <div className="mr-8">
                  <Star
                    className={number >= 3 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 4 && (
                <div className="mr-8">
                  <Star
                    className={number >= 4 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 5 && (
                <div className="mr-8">
                  <Star
                    className={number >= 5 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 6 && (
                <div className="mr-8">
                  <Star
                    className={number >= 6 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 7 && (
                <div className="mr-8">
                  <Star
                    className={number >= 7 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 8 && (
                <div className="mr-8">
                  <Star
                    className={number >= 8 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 9 && (
                <div className="mr-8">
                  <Star
                    className={number >= 9 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 10 && (
                <div className="mr-8">
                  <Star
                    className={number === 10 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
            </div>
          ) : (
            <div
              className={
                (ratingDefinitionsStatus[numberWord]?.error.message &&
                  ratingDefinitionsStatus[numberWord]?.error.message !==
                    'short') ||
                (ratingDefinitionsStatus[numberWord]?.focus &&
                  tempRatingDefinition[numberWord] !== null &&
                  tempRatingDefinition[numberWord] !== '')
                  ? 'd-f mt-16 ai-c fd-r mb-48'
                  : 'd-f mt-16 ai-c fd-r mb-12'
              }
            >
              <div className="mr-8 ml-24">
                <Circle className="CustomColor--svgFill" stroke="#949499" />
              </div>
              <div className="mr-8">
                <Circle
                  className={number >= 2 ? 'CustomColor--svgFill' : ''}
                  stroke="#949499"
                />
              </div>
              {tempRatingScale >= 3 && (
                <div className="mr-8">
                  <Circle
                    className={number >= 3 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 4 && (
                <div className="mr-8">
                  <Circle
                    className={number >= 4 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 5 && (
                <div className="mr-8">
                  <Circle
                    className={number >= 5 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 6 && (
                <div className="mr-8">
                  <Circle
                    className={number >= 6 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 7 && (
                <div className="mr-8">
                  <Circle
                    className={number >= 7 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 8 && (
                <div className="mr-8">
                  <Circle
                    className={number >= 8 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 9 && (
                <div className="mr-8">
                  <Circle
                    className={number >= 9 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
              {tempRatingScale >= 10 && (
                <div className="mr-8">
                  <Circle
                    className={number === 10 ? 'CustomColor--svgFill' : ''}
                    stroke="#949499"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );
  };

  const renderSkillsDefault = () => {
    return (
      <div className="d-f fd-r mt-6">
        <div className="mr-8">
          <Star className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Star className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Star className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Star className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Star stroke="#949499" />
        </div>
      </div>
    );
  };

  const renderCircles = () => {
    return (
      <div className="d-f fd-r mt-6">
        <div className="mr-8">
          <Circle className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Circle className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Circle className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Circle className="CustomColor--svgFill" stroke="#949499" />
        </div>
        <div className="mr-8">
          <Circle stroke="#949499" />
        </div>
      </div>
    );
  };

  const renderRatingPreview = () => {
    return (
      <div className="pt-6">
        {tempIconChoice === 'skills_default'
          ? renderSkillsDefault()
          : renderCircles()}
      </div>
    );
  };

  return (
    <>
      <div>
        <div className="fz-24 fw-b mb-12">
          {t('settings.tab.global_skills.link_text')}
        </div>
        {pageIsLoading && (
          <div>
            <Skeleton width="24%" className="mb-12" />
            <Skeleton width="90%" height={57} />
            <div className="fd-r mt-30 ai-c">
              <Skeleton width="20%" height={30} className="mr-16" />
              <Skeleton width="28%" height={44} />
            </div>
            <Skeleton width="31%" className="mt-32 mb-8" />
            <Skeleton width="70%" />
            <div className="d-f fd-c mt-8">
              {[...Array(10)].map(e => (
                <div className="fd-r mb-16">
                  <Skeleton width="2%" height={44} className="mr-4" />
                  <Skeleton width="44%" height={44} className="mr-12" />
                  <Skeleton width="44%" height={44} />
                </div>
              ))}
            </div>
            <Skeleton width={307} height={30} />
          </div>
        )}
        <>
          {!pageIsLoading && (
            <>
              <div className="d-f fd-r jc-sb ai-c">
                <div className="d-f fd-r ai-c">
                  <div
                    className={
                      !editMode
                        ? 'fz-20 fw-600 mr-36 mb-24'
                        : 'fz-20 mb-8 fw-600'
                    }
                  >
                    {t('settings.tab.global_skills.proficiency_levels')}
                  </div>
                  <div className="d-f fd-r ai-c mb-24">
                    <StatusLabel
                      id="global-skills-success"
                      success={saveSuccess}
                    />
                  </div>
                </div>
                {!editMode && (
                  <Button
                    type="button"
                    className="Button Button--linked mb-20 ml-48 mr-n-24"
                    onClick={handleEditButton}
                  >
                    <div className="d-f ai-c jc-sb">
                      <Edit3 className="c-brand-gray-darkest mr-10" size={14} />
                      {t('common.edit')}
                    </div>
                  </Button>
                )}
              </div>
              <div
                className={!editMode ? 'fz-16 mb-32 mt-2' : 'fz-16 mb-32 mt-24'}
              >
                {t('settings.tab.global_skills.specify_proficiency_levels')}
              </div>
              <div className="d-f ai-c mb-48 fw-600 c-midnight-70">
                <div>{t('settings.tab.global_skills.choose_icon')}</div>
                <InfoTooltip
                  className="ml-4 mr-36 c-midnight-50"
                  tooltip={t('settings.tab.global_skills.icon_tooltip')}
                />
                <Popover
                  isHighPriority
                  rootClassName="d-f"
                  visible={popoverOpen}
                  onClose={() => setPopoverOpen(!popoverOpen)}
                  activator={
                    <Button
                      type="button"
                      disabled={!editMode}
                      onClick={() => {
                        setPopoverOpen(!popoverOpen);
                      }}
                      className="Button Button--linked ml-37"
                    >
                      <div className="d-f fd-r ai-c">
                        {renderRatingPreview(tempIconChoice)}
                        <Icon
                          className="Select-arrowsIcon pos-i ml-12"
                          name="select_arrows"
                          aria-label="Select Arrow"
                        />
                      </div>
                    </Button>
                  }
                >
                  <div className="AppSwitcher-popover bdrs-4 f-o-n">
                    <ActionList
                      items={[
                        {
                          content: (
                            <div className="d-f fd-r">
                              {renderSkillsDefault()}
                            </div>
                          ),
                          onClick: () => {
                            setTempIconChoice('skills_default');
                            setPopoverOpen(!popoverOpen);
                          },
                        },

                        {
                          content: (
                            <div className="d-f fd-r">{renderCircles()}</div>
                          ),
                          onClick: () => {
                            setTempIconChoice('circles');
                            setPopoverOpen(!popoverOpen);
                          },
                        },
                      ]}
                    />
                  </div>
                </Popover>
              </div>
              <div className="d-f ai-c mb-12 fw-600 c-midnight-70">
                <div>{t('settings.tab.global_skills.define_levels')}</div>
                <InfoTooltip
                  className="ml-4 mr-36 c-midnight-50"
                  tooltip={t('settings.tab.global_skills.proficiency_tooltip')}
                />
              </div>
              <div>
                <p
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'settings.tab.global_skills.current_proficiency_scale',
                      {
                        skill_level_number: tempRatingScale,
                      },
                    ),
                  }}
                />
              </div>
              <div className="d-f fd-r ai-c">
                <div className="d-f fd-c">
                  {renderRatings(1, 'one')}
                  {renderRatings(2, 'two')}
                  {tempRatingScale >= 3 &&
                    (!editMode ? (
                      renderRatings(3, 'three')
                    ) : (
                      <div className="d-f fd-r ai-c">
                        {renderRatings(3, 'three')}
                        {tempRatingScale === 3 && (
                          <div
                            onClick={handleDelete}
                            onKeyDown={e => e.key === 'Enter' || e.key === ' '}
                            role="button"
                            tabIndex="0"
                          >
                            <Icon
                              name="delete"
                              className={
                                (ratingDefinitionsStatus.three?.error &&
                                  ratingDefinitionsStatus.three?.error
                                    ?.message !== 'short') ||
                                (ratingDefinitionsStatus.three?.focus &&
                                  tempRatingDefinition.three !== null &&
                                  tempRatingDefinition.three !== '')
                                  ? 'ml-10 fz-20 mb-32'
                                  : 'ml-10 fz-20'
                              }
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  {tempRatingScale >= 4 &&
                    (!editMode ? (
                      renderRatings(4, 'four')
                    ) : (
                      <div className="d-f fd-r ai-c">
                        {renderRatings(4, 'four')}
                        {tempRatingScale === 4 && (
                          <div
                            onClick={handleDelete}
                            onKeyDown={e => e.key === 'Enter' || e.key === ' '}
                            role="button"
                            tabIndex="0"
                          >
                            <Icon
                              name="delete"
                              className={
                                (ratingDefinitionsStatus.four?.error &&
                                  ratingDefinitionsStatus.four?.error
                                    ?.message !== 'short') ||
                                (ratingDefinitionsStatus.four?.focus &&
                                  tempRatingDefinition.four !== null &&
                                  tempRatingDefinition.four !== '')
                                  ? 'ml-10 fz-20 mb-32'
                                  : 'ml-10 fz-20'
                              }
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  {tempRatingScale >= 5 &&
                    (!editMode ? (
                      renderRatings(5, 'five')
                    ) : (
                      <div className="d-f fd-r ai-c">
                        {renderRatings(5, 'five')}
                        {tempRatingScale === 5 && (
                          <div
                            onClick={handleDelete}
                            onKeyDown={e => e.key === 'Enter' || e.key === ' '}
                            role="button"
                            tabIndex="0"
                          >
                            <Icon
                              name="delete"
                              className={
                                (ratingDefinitionsStatus.five?.focus &&
                                  tempRatingDefinition.five !== null &&
                                  tempRatingDefinition.five !== '') ||
                                (ratingDefinitionsStatus.five?.error &&
                                  ratingDefinitionsStatus.five?.error
                                    ?.message !== 'short')
                                  ? 'ml-10 fz-20 mb-32'
                                  : 'ml-10 fz-20'
                              }
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  {tempRatingScale >= 6 &&
                    (!editMode ? (
                      renderRatings(6, 'six')
                    ) : (
                      <div className="d-f fd-r ai-c">
                        {renderRatings(6, 'six')}
                        {tempRatingScale === 6 && (
                          <div
                            onClick={handleDelete}
                            onKeyDown={e => e.key === 'Enter' || e.key === ' '}
                            role="button"
                            tabIndex="0"
                          >
                            <Icon
                              name="delete"
                              className={
                                (ratingDefinitionsStatus.six?.focus &&
                                  tempRatingDefinition.six !== null &&
                                  tempRatingDefinition.six !== '') ||
                                (ratingDefinitionsStatus.six?.error &&
                                  ratingDefinitionsStatus.six?.error
                                    ?.message !== 'short')
                                  ? 'ml-10 fz-20 mb-32'
                                  : 'ml-10 fz-20'
                              }
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  {tempRatingScale >= 7 &&
                    (!editMode ? (
                      renderRatings(7, 'seven')
                    ) : (
                      <div className="d-f fd-r ai-c">
                        {renderRatings(7, 'seven')}
                        {tempRatingScale === 7 && (
                          <div
                            onClick={handleDelete}
                            onKeyDown={e => e.key === 'Enter' || e.key === ' '}
                            role="button"
                            tabIndex="0"
                          >
                            <Icon
                              name="delete"
                              className={
                                (ratingDefinitionsStatus.seven?.focus &&
                                  tempRatingDefinition.seven !== null &&
                                  tempRatingDefinition.seven !== '') ||
                                (ratingDefinitionsStatus.seven?.error &&
                                  ratingDefinitionsStatus.seven?.error
                                    ?.message !== 'short')
                                  ? 'ml-10 fz-20 mb-32'
                                  : 'ml-10 fz-20'
                              }
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  {tempRatingScale >= 8 &&
                    (!editMode ? (
                      renderRatings(8, 'eight')
                    ) : (
                      <div className="d-f fd-r ai-c">
                        {renderRatings(8, 'eight')}
                        {tempRatingScale === 8 && (
                          <div
                            onClick={handleDelete}
                            onKeyDown={e => e.key === 'Enter' || e.key === ' '}
                            role="button"
                            tabIndex="0"
                          >
                            <Icon
                              name="delete"
                              className={
                                (ratingDefinitionsStatus.eight?.focus &&
                                  tempRatingDefinition.eight !== null &&
                                  tempRatingDefinition.eight !== '') ||
                                (ratingDefinitionsStatus.eight?.error &&
                                  ratingDefinitionsStatus.eight?.error
                                    ?.message !== 'short')
                                  ? 'ml-10 fz-20 mb-32'
                                  : 'ml-10 fz-20'
                              }
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  {tempRatingScale >= 9 &&
                    (!editMode ? (
                      renderRatings(9, 'nine')
                    ) : (
                      <div className="d-f fd-r ai-c">
                        {renderRatings(9, 'nine')}
                        {tempRatingScale === 9 && (
                          <div
                            onClick={handleDelete}
                            onKeyDown={e => e.key === 'Enter' || e.key === ' '}
                            role="button"
                            tabIndex="0"
                          >
                            <Icon
                              name="delete"
                              className={
                                (ratingDefinitionsStatus.nine?.focus &&
                                  tempRatingDefinition.nine !== null &&
                                  tempRatingDefinition.nine !== '') ||
                                (ratingDefinitionsStatus.nine?.error &&
                                  ratingDefinitionsStatus.nine?.error
                                    ?.message !== 'short')
                                  ? 'ml-10 fz-20 mb-32'
                                  : 'ml-10 fz-20'
                              }
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  {tempRatingScale >= 10 &&
                    (!editMode ? (
                      renderRatings(10, 'ten')
                    ) : (
                      <div className="d-f fd-r ai-c">
                        {renderRatings(10, 'ten')}
                        {tempRatingScale === 10 && (
                          <div
                            onClick={handleDelete}
                            onKeyDown={e => e.key === 'Enter' || e.key === ' '}
                            role="button"
                            tabIndex="0"
                          >
                            <Icon
                              name="delete"
                              className={
                                (ratingDefinitionsStatus.ten?.focus &&
                                  tempRatingDefinition.ten !== null &&
                                  tempRatingDefinition.ten !== '') ||
                                (ratingDefinitionsStatus.ten?.error &&
                                  ratingDefinitionsStatus.ten?.error
                                    ?.message !== 'short')
                                  ? 'ml-10 fz-20 mb-32'
                                  : 'ml-10 fz-20'
                              }
                            />
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </>
          )}
        </>
        {editMode && (
          <>
            {minLevelMessage && (
              <div className="d-f ai-c mt-8">
                <Text variant={TEXT_VARIANTS.BODY}>
                  {t('settings.tab.global_skills.min_proficiency_levels')}
                </Text>
              </div>
            )}
            {maxLevelMessage && (
              <div className="d-f ai-c mt-8">
                <Text variant={TEXT_VARIANTS.BODY}>
                  {t('settings.tab.global_skills.max_proficiency_levels')}
                </Text>
              </div>
            )}
            {levelButtonShowing && (
              <div className="d-f ai-c mt-16">
                <Icon disabled name="add" className="c-brand-blue mr-2 mt-1" />
                <button
                  type="button"
                  className="IconButton"
                  onClick={handleAddLevel}
                >
                  <Text variant={TEXT_VARIANTS.BODY_LINK}>
                    {t('settings.tab.global_skills.add_level')}
                  </Text>
                </button>
              </div>
            )}
            <div className="d-f fd-r jc-e mt-48">
              <ButtonGroup>
                <Button
                  type="button"
                  className="Button mr-12"
                  onClick={handleCancel}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="button"
                  disabled={shouldDisableSave}
                  className="Button Button--customColor w-125"
                  onClick={handleSubmit}
                >
                  {t('common.save')}
                </Button>
              </ButtonGroup>
            </div>
          </>
        )}
        {saveClicked && (
          <ConfirmationModal
            saveClicked={saveClicked}
            closeModal={closeModal}
            tempIconChoice={tempIconChoice}
            trimTempRatingDefinitions={trimTempRatingDefinitions}
            tempRatingDefinition={tempRatingDefinition}
            tempRatingScale={tempRatingScale}
            setEditMode={setEditMode}
            currentCompanyRatingScale={currentCompanyRatingScale}
            setSaveSuccess={setSaveSuccess}
          />
        )}
      </div>
    </>
  );
};

export default withSkillsClientWrapper(
  withReactQueryWrapper(GlobalSkillsSettings),
);
